import { useLayoutEffect, useRef, useState } from 'react';
import { registerBlock } from '../blockRegistry';
import { DEFAULT_FRAMEWORK_MATRIX } from '../constants';

/**
 * Premium, universal "framework-matrix" block (Phase 3.3 / Matrix v1).
 *
 * A presentation-quality 2-axis positioning matrix that users fill with their
 * own methodology. No industry/quadrant/count assumptions. Persisted as an
 * opaque block `content` object (no backend change). Registered via the
 * Phase 3.1 registry so all three render paths pick it up through delegation.
 *
 * content shape:
 *   {
 *     title, description,
 *     xAxis: { title, lowLabel, highLabel },
 *     yAxis: { title, lowLabel, highLabel },
 *     items: [ { id, title, description, x, y, color, preset } ]
 *   }
 */

// Restrained, coordinated accent palette (NOT brand-hardcoded). Cycled as a
// tasteful default when an item has no explicit color.
const ACCENTS = ['#4F6D7A', '#A15C4A', '#5E8C61', '#7A6F9B', '#B08968', '#3F7C8C'];

const clamp = (n) => Math.max(0, Math.min(100, Number.isFinite(+n) ? +n : 50));

function withDefaults(content) {
  const c = content || {};
  return {
    title: c.title ?? DEFAULT_FRAMEWORK_MATRIX.title,
    description: c.description ?? '',
    xAxis: { title: '', lowLabel: '', highLabel: '', ...(c.xAxis || {}) },
    yAxis: { title: '', lowLabel: '', highLabel: '', ...(c.yAxis || {}) },
    items: Array.isArray(c.items) ? c.items : [],
  };
}

function accentFor(item, idx) {
  return item.color || ACCENTS[idx % ACCENTS.length];
}

// ---------------------------------------------------------------- VIEW
const PAD = 26; // inner plot padding (px) so 0/100 markers stay inside the frame
const MARKER = 26; // numbered marker diameter (px)

function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function MatrixGrid({ W, H }) {
  const iw = W - PAD * 2;
  const ih = H - PAD * 2;
  return (
    <svg className="absolute inset-0 pointer-events-none" width={W} height={H} aria-hidden="true">
      <rect x={PAD} y={PAD} width={iw / 2} height={ih / 2} fill="#F8FAFC" />
      <rect x={PAD + iw / 2} y={PAD + ih / 2} width={iw / 2} height={ih / 2} fill="#F8FAFC" />
      {[0.25, 0.75].map((f) => (
        <g key={f} stroke="#EEF2F6" strokeWidth="1">
          <line x1={PAD + iw * f} y1={PAD} x2={PAD + iw * f} y2={H - PAD} />
          <line x1={PAD} y1={PAD + ih * f} x2={W - PAD} y2={PAD + ih * f} />
        </g>
      ))}
      <g stroke="#CBD5E1" strokeWidth="1" strokeDasharray="4 4">
        <line x1={PAD + iw / 2} y1={PAD} x2={PAD + iw / 2} y2={H - PAD} />
        <line x1={PAD} y1={PAD + ih / 2} x2={W - PAD} y2={PAD + ih / 2} />
      </g>
      {/* Axes with arrowheads: X grows right, Y grows up */}
      <g stroke="#64748B" strokeWidth="1.5" fill="none" strokeLinecap="round">
        <line x1={PAD} y1={H - PAD} x2={W - PAD + 8} y2={H - PAD} />
        <line x1={PAD} y1={H - PAD} x2={PAD} y2={PAD - 8} />
        <polyline points={`${W - PAD + 2},${H - PAD - 5} ${W - PAD + 8},${H - PAD} ${W - PAD + 2},${H - PAD + 5}`} />
        <polyline points={`${PAD - 5},${PAD - 2} ${PAD},${PAD - 8} ${PAD + 5},${PAD - 2}`} />
      </g>
    </svg>
  );
}

function MatrixPlot({ c, points, selectedId, onSelect }) {
  const [ref, W] = useElementWidth();
  const H = W ? Math.round(W < 480 ? W * 0.95 : W * 0.6) : 0;
  const iw = W - PAD * 2;
  const ih = H - PAD * 2;
  const placedPts = points.map((p) => ({ ...p, px: PAD + (p.x / 100) * iw, py: PAD + ((100 - p.y) / 100) * ih }));

  return (
    <div className="flex gap-1.5">
      <div className="flex items-center justify-center w-5 sm:w-7 flex-shrink-0">
        {c.yAxis.title && (
          <span data-testid="matrix-y-axis-title" className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 whitespace-nowrap -rotate-90">
            {c.yAxis.title} <span aria-hidden="true">→</span>
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div ref={ref} data-testid="matrix-plot" className="relative w-full rounded-xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden"
          style={{ height: H || undefined, aspectRatio: H ? undefined : '1 / 1' }}>
          {W > 0 && <MatrixGrid W={W} H={H} />}
          {c.yAxis.highLabel && <span className="absolute left-[34px] top-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">{c.yAxis.highLabel}</span>}
          {c.yAxis.lowLabel && <span className="absolute left-[34px] bottom-[30px] text-[10px] font-medium uppercase tracking-wider text-slate-400">{c.yAxis.lowLabel}</span>}
          {W > 0 && placedPts.map((p) => {
            const isActive = p.id === selectedId;
            const dim = selectedId && !isActive;
            return (
              <button key={p.id} type="button" data-testid={`matrix-item-${p.id}`} data-x={p.x} data-y={p.y}
                data-selected={isActive ? 'true' : 'false'} aria-pressed={isActive}
                aria-label={p.title}
                onClick={() => onSelect(isActive ? null : p.id)}
                className="absolute rounded-full flex items-center justify-center text-[11px] font-bold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                style={{
                  left: p.px - MARKER / 2, top: p.py - MARKER / 2, width: MARKER, height: MARKER,
                  backgroundColor: p.color, zIndex: isActive ? 30 : 20, opacity: dim ? 0.45 : 1,
                  boxShadow: isActive ? `0 0 0 2px #fff, 0 0 0 3.5px ${p.color}` : '0 0 0 2px #fff, 0 1px 3px rgba(15,23,42,0.18)',
                  transition: 'opacity 200ms ease, box-shadow 200ms ease',
                }}>
                {p.n}
              </button>
            );
          })}
          {points.length === 0 && (
            <p className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">No items plotted yet</p>
          )}
        </div>
        <div className="flex justify-between mt-1.5 px-[18px] text-[10px] font-medium uppercase tracking-wider text-slate-400">
          <span>{c.xAxis.lowLabel}</span>
          <span>{c.xAxis.highLabel}</span>
        </div>
        {c.xAxis.title && (
          <p data-testid="matrix-x-axis-title" className="text-center mt-0.5 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
            {c.xAxis.title} <span aria-hidden="true">→</span>
          </p>
        )}
      </div>
    </div>
  );
}

function MatrixDetail({ item }) {
  if (!item) {
    return <p data-testid="matrix-detail-hint" className="text-center text-xs text-slate-400 py-3">Tap a marker to see its details</p>;
  }
  return (
    <div data-testid="matrix-detail" className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
      style={{ borderLeft: `3px solid ${item.color}` }}>
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: item.color }}>{item.n}</span>
        <div className="min-w-0">
          <p data-testid="matrix-detail-title" className="font-medium text-slate-800 break-words">{item.title}</p>
          {item.description && (
            <p data-testid="matrix-detail-description" className="text-sm text-slate-600 mt-1 leading-relaxed">{item.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MatrixLegend({ points, selectedId, onSelect }) {
  if (points.length === 0) return null;
  return (
    <ol data-testid="matrix-legend" className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3">
      {points.map((p) => {
        const isActive = p.id === selectedId;
        return (
          <li key={p.id}>
            <button type="button" data-testid={`matrix-legend-item-${p.id}`} onClick={() => onSelect(isActive ? null : p.id)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[13px] transition-colors ${isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}>
              <span className="w-[18px] h-[18px] rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: p.color }}>{p.n}</span>
              <span className="min-w-0 break-words">{p.title}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export function FrameworkMatrixView({ block }) {
  const c = withDefaults(block.content);
  const [selectedId, setSelectedId] = useState(null); // local presentation state only
  const points = c.items.map((it, idx) => ({
    id: it.id, n: idx + 1, title: it.title || 'Untitled', description: it.description || '',
    x: clamp(it.x), y: clamp(it.y), color: accentFor(it, idx),
  }));
  const selected = points.find((p) => p.id === selectedId) || null;

  return (
    <div data-testid="framework-matrix-view" className="w-full py-6">
      <div className="max-w-4xl mx-auto px-1 sm:px-2">
        {c.title && (
          <h3 className="text-center font-serif text-2xl sm:text-3xl text-slate-800 tracking-tight">{c.title}</h3>
        )}
        {c.description && (
          <p className="text-center text-sm sm:text-base text-slate-500 mt-2 max-w-2xl mx-auto leading-relaxed">{c.description}</p>
        )}
        <div className="mt-6 sm:mt-8">
          <MatrixPlot c={c} points={points} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
        <div className="mt-4 max-w-2xl mx-auto">
          <MatrixDetail item={selected} />
          <MatrixLegend points={points} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- EDITOR
export function FrameworkMatrixEditor({ block, isEditing, onUpdate }) {
  const c = withDefaults(block.content);

  const update = (patch) => onUpdate({ ...block, content: { ...c, ...patch } });
  const updateAxis = (axis, patch) => update({ [axis]: { ...c[axis], ...patch } });
  const updateItem = (id, patch) =>
    update({ items: c.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) });
  const addItem = () =>
    update({
      items: [
        ...c.items,
        { id: `m${Date.now()}${Math.floor(Math.random() * 1000)}`, title: 'New item', description: '', x: 50, y: 50, color: '' },
      ],
    });
  const removeItem = (id) => update({ items: c.items.filter((it) => it.id !== id) });

  // When not actively editing this block, show the polished presentation.
  if (!isEditing) return <FrameworkMatrixView block={block} />;

  const inp = 'w-full px-2 py-1.5 border border-slate-200 rounded text-sm';
  const lbl = 'block text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-1';

  return (
    <div data-testid="framework-matrix-editor" className="space-y-5 p-4 bg-slate-50/60 rounded-lg border border-slate-200">
      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className={lbl}>Matrix title</label>
          <input className={inp} data-testid="matrix-title-input" value={c.title}
            onChange={(e) => update({ title: e.target.value })} placeholder="e.g. Prioritization Matrix" />
        </div>
        <div>
          <label className={lbl}>Description (optional)</label>
          <textarea className={inp} data-testid="matrix-description-input" rows={2} value={c.description}
            onChange={(e) => update({ description: e.target.value })} placeholder="A short framing sentence" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {['xAxis', 'yAxis'].map((axis) => (
          <div key={axis} className="space-y-2 p-3 bg-white rounded border border-slate-200">
            <p className="text-xs font-semibold text-slate-600">{axis === 'xAxis' ? 'Horizontal axis' : 'Vertical axis'}</p>
            <input className={inp} data-testid={`matrix-${axis}-title`} value={c[axis].title}
              onChange={(e) => updateAxis(axis, { title: e.target.value })} placeholder="Axis title" />
            <div className="grid grid-cols-2 gap-2">
              <input className={inp} value={c[axis].lowLabel}
                onChange={(e) => updateAxis(axis, { lowLabel: e.target.value })} placeholder="Low label" />
              <input className={inp} value={c[axis].highLabel}
                onChange={(e) => updateAxis(axis, { highLabel: e.target.value })} placeholder="High label" />
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-600">Items ({c.items.length})</p>
          <button type="button" data-testid="matrix-add-item" onClick={addItem}
            className="text-xs px-3 py-1.5 rounded bg-slate-800 text-white hover:bg-slate-700">
            + Add item
          </button>
        </div>
        {c.items.map((item) => (
          <div key={item.id} data-testid={`matrix-item-editor-${item.id}`} className="p-3 bg-white rounded border border-slate-200 space-y-2">
            <div className="flex items-center gap-2">
              <input className={inp} data-testid={`matrix-item-title-${item.id}`} value={item.title}
                onChange={(e) => updateItem(item.id, { title: e.target.value })} placeholder="Item title" />
              <input type="color" value={item.color || '#4F6D7A'} title="Accent color"
                data-testid={`matrix-item-color-${item.id}`}
                onChange={(e) => updateItem(item.id, { color: e.target.value })}
                className="w-9 h-9 rounded border border-slate-200 cursor-pointer flex-shrink-0" />
              <button type="button" data-testid={`matrix-item-remove-${item.id}`} onClick={() => removeItem(item.id)}
                className="text-slate-400 hover:text-red-500 text-sm px-2 flex-shrink-0">✕</button>
            </div>
            <textarea className={inp} rows={2} value={item.description || ''}
              onChange={(e) => updateItem(item.id, { description: e.target.value })} placeholder="Description (optional)" />
            <div className="grid grid-cols-2 gap-3">
              <label className="text-[11px] text-slate-500">
                X: {clamp(item.x)}
                <input type="range" min="0" max="100" value={clamp(item.x)} data-testid={`matrix-item-x-${item.id}`}
                  onChange={(e) => updateItem(item.id, { x: +e.target.value })} className="w-full" />
              </label>
              <label className="text-[11px] text-slate-500">
                Y: {clamp(item.y)}
                <input type="range" min="0" max="100" value={clamp(item.y)} data-testid={`matrix-item-y-${item.id}`}
                  onChange={(e) => updateItem(item.id, { y: +e.target.value })} className="w-full" />
              </label>
            </div>
          </div>
        ))}
        {c.items.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-3">No items yet — add your first one.</p>
        )}
      </div>

      {/* Live preview */}
      <div className="pt-2 border-t border-slate-200">
        <p className={lbl}>Preview</p>
        <FrameworkMatrixView block={block} />
      </div>
    </div>
  );
}

registerBlock({
  type: 'framework-matrix',
  getDefaultContent: () => DEFAULT_FRAMEWORK_MATRIX,
  Editor: FrameworkMatrixEditor,
  View: FrameworkMatrixView,
});
